import React, { createContext, useContext, useState } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { theme } from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import { Layout } from './components/Layout';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import ClaimsManagement from './components/ClaimsManagement';
import ContactManagement from './components/ContactManagement';
import ContactDetails from './pages/ContactDetails';
import InsurersManagement from './components/InsurersManagement';
import InsurerDetails from './pages/InsurerDetails';
import UserManagement from './components/UserManagement';
import ClientManagement from './components/ClientManagement';
import ClientDetails from './pages/ClientDetails';
import LeadsManagement from './components/LeadsManagement';
import MarketingManagement from './components/MarketingManagement';
import Calendar from './components/Calendar';
import CompanyManagement from './components/CompanyManagement';
import CompanyDetails from './pages/CompanyDetails';
import InsuranceRepresentatives from './pages/InsuranceRepresentatives';
import InsuranceRepresentativeDetails from './pages/InsuranceRepresentativeDetails';
import InsuranceRepManagement from './components/InsuranceRepManagement';
import RSSFeeds from './components/tools/RSSFeeds';
import WeatherFeeds from './components/tools/WeatherFeeds';
import ClaimsMap from './components/ClaimsMap';
import Settings from './components/settings/Settings';
import RolesManagement from './components/settings/RolesManagement';
import CompanySettings from './components/settings/CompanySettings';
import EmailTemplates from './components/settings/EmailTemplates';
import Integrations from './components/settings/Integrations';
import Help from './components/help/Help';
import Documentation from './components/help/Documentation';
import FAQ from './components/help/FAQ';
import Support from './components/help/Support';
import Feedback from './components/help/Feedback';
import Profile from './components/Profile';
import Dashboard from './components/Dashboard';
import DashboardAlternative from './components/DashboardAlternative';
import OwnerDashboard from './components/owner/OwnerDashboard';
import ClaimDetails from './components/ClaimDetails';
import ClaimsLayout from './app/claims/layout';
import NewClaimPage from './pages/NewClaimPage';
import NewClientPage from './pages/NewClientPage';
import ScheduleEventPage from './pages/ScheduleEventPage';
import AddContactPage from './pages/AddContactPage';
import AddLeadPage from './pages/AddLeadPage';
import LeadDetails from './pages/LeadDetails';
import PerformanceDebugger from './components/debug/PerformanceDebugger';
import ImportPage from './components/file/ImportPage';
import ExportPage from './components/file/ExportPage';
import BackupPage from './components/file/BackupPage';
import RestorePage from './components/file/RestorePage';

// Global breadcrumb context
interface BreadcrumbContextType {
  lastItem: string | undefined;
  setLastItem: (item: string | undefined) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType>({
  lastItem: undefined,
  setLastItem: () => {},
});

export const useBreadcrumb = () => useContext(BreadcrumbContext);

const BreadcrumbProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lastItem, setLastItem] = useState<string | undefined>(undefined);
  
  return (
    <BreadcrumbContext.Provider value={{ lastItem, setLastItem }}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

const PrivateRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return element;
};

// Wrapper for ClaimDetails to provide file number to Layout
const ClaimDetailsWithBreadcrumb: React.FC = () => {
  const { fileNumber } = useParams<{ fileNumber: string }>();
  return <ClaimDetails />;
};

// Wrapper for ContactDetails to provide contact name to Layout
const ContactDetailsWithBreadcrumb: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <ContactDetails />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BreadcrumbProvider>
          <QueryClientProvider client={queryClient}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<PrivateRoute element={<Layout />} />}>
                <Route index element={<Dashboard />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="dashboard-alternative" element={<DashboardAlternative />} />
                <Route path="claims" element={<ClaimsLayout />}>
                  <Route index element={<ClaimsManagement />} />
                  <Route path="new" element={<NewClaimPage />} />
                  <Route path=":fileNumber" element={<ClaimDetailsWithBreadcrumb />} />
                </Route>
                <Route path="clients" element={<ClientManagement />} />
                <Route path="clients/new" element={<NewClientPage />} />
                <Route path="clients/:id" element={<ClientDetails />} />
                <Route path="contacts" element={<ContactManagement />} />
                <Route path="contacts/new" element={<AddContactPage />} />
                <Route path="contacts/:id" element={<ContactDetailsWithBreadcrumb />} />
                <Route path="companies" element={<CompanyManagement />} />
                <Route path="companies/:id" element={<CompanyDetails />} />
                <Route path="insurers" element={<InsurersManagement />} />
                <Route path="insurers/:id" element={<InsurerDetails />} />
                <Route path="insurance-representatives" element={<InsuranceRepresentatives />} />
                <Route path="insurance-representatives/:id" element={<InsuranceRepresentativeDetails />} />
                <Route path="insurance-reps" element={<InsuranceRepManagement />} />
                <Route path="leads" element={<LeadsManagement />} />
                <Route path="leads/new" element={<AddLeadPage />} />
                <Route path="leads/:id" element={<LeadDetails />} />
                <Route path="marketing" element={<MarketingManagement />} />
                <Route path="calendar" element={<Calendar />} />
                <Route path="calendar/new" element={<ScheduleEventPage />} />
                <Route path="profile" element={<Profile />} />

                {/* Settings routes */}
                <Route path="settings" element={<Settings />}>
                  <Route index element={<Navigate to="users" />} />
                  <Route path="users" element={<UserManagement />} />
                  <Route path="roles" element={<RolesManagement />} />
                  <Route path="company" element={<CompanySettings />} />
                  <Route path="email-templates" element={<EmailTemplates />} />
                  <Route path="integrations" element={<Integrations />} />
                </Route>

                {/* Help routes */}
                <Route path="help" element={<Help />}>
                  <Route index element={<Navigate to="documentation" />} />
                  <Route path="documentation" element={<Documentation />} />
                  <Route path="faq" element={<FAQ />} />
                  <Route path="support" element={<Support />} />
                  <Route path="feedback" element={<Feedback />} />
                </Route>

                {/* Tools Routes */}
                <Route path="tools">
                  <Route index element={<Navigate to="/tools/news" replace />} />
                  <Route path="news" element={<RSSFeeds />} />
                  <Route path="weather" element={<WeatherFeeds />} />
                  <Route path="claims-map" element={<ClaimsMap />} />
                </Route>

                {/* File Management Routes */}
                <Route path="file">
                  <Route index element={<Navigate to="/file/import" replace />} />
                  <Route path="import" element={<ImportPage />} />
                  <Route path="export" element={<ExportPage />} />
                  <Route path="backup" element={<BackupPage />} />
                  <Route path="restore" element={<RestorePage />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
              <Route path="/owner/*" element={<PrivateRoute element={<OwnerDashboard />} />} />
            </Routes>
            {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
            <PerformanceDebugger />
          </QueryClientProvider>
        </BreadcrumbProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App; 